import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, Grid
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';
import { useGuiaEntradaStore } from '../store/guiaEntradaStore';
import { guiaEntradaService } from '../services/guiaEntradaService';
import { initialGuiaEntradaState } from '../types/guiaEntrada.types';
import { almacenService } from '../../almacenes/services/almacenService';
import type { Deposito } from '../../almacenes/types/almacen.types';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const GuiasEntradaPage = () => {
  const { guias, loading, setGuias, setLoading } = useGuiaEntradaStore();
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState(initialGuiaEntradaState);

  useEffect(() => { loadData(); loadDepositos(); }, []);

  const loadDepositos = async () => {
    try { setDepositos(await almacenService.getAll()); } catch { /* handled */ }
  };

  const loadData = async () => {
    setLoading(true);
    try { setGuias(await guiaEntradaService.getAll()); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      await guiaEntradaService.save(formData);
      setOpenDialog(false);
      setFormData(initialGuiaEntradaState);
      await loadData();
    } catch { /* handled */ }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar esta guía de entrada?')) return;
    try { await guiaEntradaService.delete(id); await loadData(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'idMovimiento', headerName: 'ID', width: 70 },
    { field: 'numeroGuia', headerName: 'N° Guía', width: 150 },
    { field: 'depositoDestinoNombre', headerName: 'Almacén Destino', width: 150 },
    { field: 'transportistaDescripcion', headerName: 'Transportista', flex: 1, minWidth: 180 },
    { field: 'placaVehiculo', headerName: 'Placa', width: 100 },
    { field: 'choferNombre', headerName: 'Chofer', width: 150 },
    { field: 'fechaLlegada', headerName: 'Fecha Llegada', width: 130,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '-' },
    { field: 'estado', headerName: 'Estado', width: 100,
      renderCell: (params) => <Chip label={params.value === 'V' ? 'Vigente' : 'Anulado'} size="small" color={params.value === 'V' ? 'success' : 'error'} />
    },
    { field: 'actions', headerName: 'Acciones', width: 100, sortable: false,
      renderCell: (params) => (
        <Button size="small" color="error" onClick={() => handleDelete(params.row.idMovimiento)}>Eliminar</Button>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Guías de Entrada</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData(initialGuiaEntradaState); setOpenDialog(true); }}>
          Nueva Guía
        </Button>
      </Box>

      <Paper sx={{ height: 500 }}>
        <DataGrid rows={guias} columns={columns} getRowId={(row) => row.idMovimiento}
          loading={loading} density="compact"
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nueva Guía de Entrada</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="N° Guía" value={formData.numeroGuia ?? ''}
                onChange={(e) => setFormData({ ...formData, numeroGuia: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Almacén Destino</InputLabel>
                <Select value={formData.idDepositoDestino ?? ''} label="Almacén Destino"
                  onChange={(e) => setFormData({ ...formData, idDepositoDestino: Number(e.target.value) })}>
                  {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Transportista" value={formData.transportistaDescripcion ?? ''}
                onChange={(e) => setFormData({ ...formData, transportistaDescripcion: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Placa Vehículo" value={formData.placaVehiculo ?? ''}
                onChange={(e) => setFormData({ ...formData, placaVehiculo: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Chofer Nombre" value={formData.choferNombre ?? ''}
                onChange={(e) => setFormData({ ...formData, choferNombre: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Chofer Documento" value={formData.choferDocumento ?? ''}
                onChange={(e) => setFormData({ ...formData, choferDocumento: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Fecha Llegada" type="date"
                value={formData.fechaLlegada ?? ''} InputLabelProps={{ shrink: true }}
                onChange={(e) => setFormData({ ...formData, fechaLlegada: e.target.value || null })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observaciones" multiline rows={2}
                value={formData.observaciones ?? ''}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuiasEntradaPage;
